import { createSelector } from 'reselect';
import { RootState, SettingsState, EditableAlarmSetting } from '../model/state';
import { AlarmSettings, AlarmSeverity, SystemMode, DeliveryMediumEnabled, AlarmSettingStatus, Alarm, Device } from 'model';
import { groupBy, toPairs } from 'lodash';

/**
 * Direct selector to the state
 */
const selectSettings = () => (state: RootState) => state.settings;

/**
 * Other specific selectors
 */
const selectIsSubmittingThresholds = () => createSelector(
  selectSettings(),
  (settings: SettingsState) => settings.thresholds.isSubmitting
);

const selectThresholdSettings = () => createSelector(
  selectSettings(),
  (settings: SettingsState) => settings.thresholds.settings
);

const selectAlarmSettings = () => createSelector(
  selectSettings(),
  (settings: SettingsState) => settings.notifications.settings
);

const selectCurrentAlarmSeverityTab = () => createSelector(
  selectSettings(),
  (settings: SettingsState) => settings.notifications.currentTab
);

const selectIsLoadingAlarmSettings = () => createSelector(
  selectSettings(),
  (settings: SettingsState) => settings.notifications.isLoading
);

const selectCurrentAlarmSettingDevice = () => createSelector(
  selectSettings(),
  (settings: SettingsState) => settings.notifications.device
);

type SettingsMerger = {
  alarm: Alarm | undefined,
  smsAnd: boolean,
  smsOr: boolean,
  callAnd: boolean,
  callOr: boolean,
  emailAnd: boolean,
  emailOr: boolean,
  pushAnd: boolean,
  pushOr: boolean,
}

const toDeliveryMediumEnabled = (and: boolean, or: boolean): DeliveryMediumEnabled => {
  switch (true) {
    case (and === true && or === true):
      return DeliveryMediumEnabled.TRUE;
    case (and === false && or === false):
      return DeliveryMediumEnabled.FALSE;
    default:
      return DeliveryMediumEnabled.MIXED;
  }
}

const selectAlarmSettingsView = () => createSelector(
  selectAlarmSettings(),
  selectCurrentAlarmSeverityTab(),
  selectCurrentAlarmSettingDevice(),
  (settings: EditableAlarmSetting<AlarmSettings>[], severity: AlarmSeverity, device?: Device) => {

    const filtered = settings.filter(s =>
        s.alarm.severity === severity &&
        s.systemMode === SystemMode.HOME &&
        (device === undefined || device.type === s.alarm.deviceType)
      );

    const groupedByAlarm = toPairs(groupBy(filtered, s => s.alarm.id));
    const result: AlarmSettingStatus[] = groupedByAlarm.map(([alarmId, settings]: [string, EditableAlarmSetting<AlarmSettings>[]]) => {
      const merge: SettingsMerger = settings.reduce((acc: SettingsMerger, s: EditableAlarmSetting<AlarmSettings>) => {
        return {   
          alarm: s.alarm,
          smsAnd: acc.smsAnd && (s.smsEnabled === undefined ? true : s.smsEnabled),
          smsOr: acc.smsOr || (s.smsEnabled === undefined ? false : s.smsEnabled),
          callAnd: acc.callAnd && (s.callEnabled === undefined ? true : s.callEnabled),
          callOr: acc.callOr || (s.callEnabled === undefined ? false : s.callEnabled),
          pushAnd: acc.pushAnd && (s.pushEnabled === undefined ? true : s.pushEnabled),
          pushOr: acc.pushOr || (s.pushEnabled === undefined ? false : s.pushEnabled),
          emailAnd: acc.emailAnd && (s.emailEnabled === undefined ? true : s.emailEnabled),
          emailOr: acc.emailOr || (s.emailEnabled === undefined ? false : s.emailEnabled)
        }
      }, {
        smsAnd: true,
        smsOr: false,
        callAnd: true,
        callOr: false,
        emailAnd: true,
        emailOr: false,
        pushAnd: true,
        pushOr: false,
      } as SettingsMerger)

      return {
        alarm: merge.alarm,
        sms: toDeliveryMediumEnabled(merge.smsAnd, merge.smsOr),
        push: toDeliveryMediumEnabled(merge.pushAnd, merge.pushOr),
        call: toDeliveryMediumEnabled(merge.callAnd, merge.callOr),
        email: toDeliveryMediumEnabled(merge.emailAnd, merge.emailOr),
        isNested: false,
      } as AlarmSettingStatus
    })

    // re order items to show nested alarms together
    return result
      .reduce((acc: AlarmSettingStatus[], val: AlarmSettingStatus) => {
        if (val.alarm.triggersAlarm) {
          const child = result.find(datum => datum.alarm.id === val.alarm.triggersAlarm);
          return child ? [ ...acc, val, { ...child, isNested: true, alarm: { ...child.alarm, triggeredBy: undefined }} ] : [ ...acc, val ]
        } else {
          return [ ...acc, val ]
        }
      }, [])
      .filter(datum => datum.alarm.triggeredBy === undefined)

  }
);

const selectDirtyAlarmSettings = () => createSelector(
  selectAlarmSettings(),
  (settings: EditableAlarmSetting<AlarmSettings>[]) => settings.filter(s => s.isDirty) as AlarmSettings[]
);

const selectIsSubmittingAlertSettings = () => createSelector(
  selectSettings(),
  (settings: SettingsState) => settings.notifications.isSubmitting
);

const selectHasDirtyAlarmSettings = () => createSelector(
  selectDirtyAlarmSettings(),
  (settings: AlarmSettings[]) => settings.length > 0
);

export default selectSettings;
export {
  selectIsSubmittingThresholds,
  selectThresholdSettings,
  selectCurrentAlarmSeverityTab,
  selectAlarmSettingsView,
  selectDirtyAlarmSettings,
  selectIsSubmittingAlertSettings,
  selectHasDirtyAlarmSettings,
  selectIsLoadingAlarmSettings,
}
